import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';

/**
 * Helper pra padronizar mutations com atualização otimista.
 *
 * - `onMutate`: atualiza o cache ANTES do servidor responder (UI instantânea)
 * - `onError`: reverte pro estado anterior se a mutation falhar (rollback)
 * - `onSettled`: invalida a query depois pra sincronizar com Realtime / server truth
 *
 * Use sempre que a mutation puder ser "prevista" no cliente (toggle, update de
 * campo, delete de item). Evite pra mutations que dependem de dados do servidor
 * pra calcular o novo estado (ex: criar registro cujo ID vem do servidor).
 *
 * @example
 * ```tsx
 * const toggle = useOptimisticMutation<{ id: string; enabled: boolean }, Field[]>({
 *   queryKey: ['custom-fields'],
 *   mutationFn: (vars) => api.toggle(vars),
 *   applyOptimistic: (fields, vars) =>
 *     fields.map(f => f.id === vars.id ? { ...f, enabled: vars.enabled } : f),
 *   onSuccessMessage: 'Campo atualizado',
 *   onErrorMessage: 'Erro ao atualizar',
 * });
 *
 * toggle.mutate({ id: '123', enabled: false });
 * ```
 */
export interface UseOptimisticMutationOptions<TVars, TCache>
  extends Omit<
    UseMutationOptions<unknown, Error, TVars, { previous: TCache | undefined }>,
    'mutationFn' | 'onMutate' | 'onError' | 'onSettled'
  > {
  /** Chave da query que contém os dados a serem atualizados otimisticamente */
  queryKey: QueryKey;

  /** Chamada ao servidor */
  mutationFn: (vars: TVars) => Promise<unknown>;

  /**
   * Aplica o efeito otimista no cache.
   * Recebe o valor atual do cache (pode ser `undefined` se nunca buscou) e as
   * variáveis da mutation. Retorna o novo valor do cache.
   */
  applyOptimistic: (previous: TCache | undefined, vars: TVars) => TCache | undefined;

  /**
   * Se `true` (default), invalida a query após a mutation terminar (sucesso ou
   * erro) pra sincronizar com o servidor. Desligue quando Realtime já sincroniza
   * ou quando o cache otimista é confiável.
   */
  invalidateOnSettled?: boolean;
}

export function useOptimisticMutation<TVars, TCache>({
  queryKey,
  mutationFn,
  applyOptimistic,
  invalidateOnSettled = true,
  ...rest
}: UseOptimisticMutationOptions<TVars, TCache>) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, TVars, { previous: TCache | undefined }>({
    ...rest,
    mutationFn,

    onMutate: async (vars) => {
      // Cancela requisições em voo pra evitar sobrescrever o cache otimista
      await queryClient.cancelQueries({ queryKey });

      // Snapshot do cache atual pra rollback
      const previous = queryClient.getQueryData<TCache>(queryKey);

      // Aplica o efeito otimista
      queryClient.setQueryData<TCache | undefined>(queryKey, (old) =>
        applyOptimistic(old as TCache | undefined, vars),
      );

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      // Rollback se tivermos snapshot
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },

    onSettled: () => {
      if (invalidateOnSettled) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
